import logger from '@utils/logger';
import {
  GetBatchAssortmentSwapRecommendationsInput,
  useGetBatchAssortmentSwapRecommendationsQuery,
} from '@graphql/generated/graphql';
import { useEffect, useMemo } from 'react';
import { useAppSelector } from '@store/hooks';
import { selectProgram } from '@store/slices/programSlice';
import { AgileRecommendationResult, AgileRecommendationTypes } from '@components/agile/types/AgileRecommendationTypes';

const useGetBatchAssortmentSwapRecommendations = (input: GetBatchAssortmentSwapRecommendationsInput) => {
  const selectedProgramName = useAppSelector(selectProgram);
  const { ...restInput } = input;
  const { data, isLoading, isSuccess } = useGetBatchAssortmentSwapRecommendationsQuery(
    {
      input: {
        ...restInput,
      },
    },
    {
      // disable refetchOnWindowFocus so that data is not refreshed
      // when user leave the page and come back
      refetchOnWindowFocus: false,
    },
  );

  const recommendations: AgileRecommendationResult[] | undefined = useMemo(
    () =>
      data?.getBatchAssortmentSwapRecommendations.swapRecommendations?.map((innerList) => {
        const selectionsInZone = input.batchAssortmentSwap.find(
          (item) => item.macroplanogramId === innerList.macroplanogramId,
        )?.addAssortment;
        const unitsToRemove = Number(innerList.facingUnitsToRemove);
        const maxFacingUnits = Number(innerList.maxFacingUnits);
        const occupiedFacingUnits = Number(innerList.occupiedFacingUnits);
        const { macroplanogramId } = innerList;
        const results = innerList.swapRecommendations.map<AgileRecommendationTypes>((item) => {
          const isFacingUnitsZero = Number(selectionsInZone?.find((obj) => item.asin === obj.asin)?.facingUnits) === 0;
          return {
            entryId: item.entryId,
            asin: item.asin,
            subcategoryId: item.subcategoryId ?? undefined, // Include subcategoryId for filtering
            availableFacings: Number(item.facingUnits),
            facingToRemove: isFacingUnitsZero ? Number(item.facingUnits) * -1 : 0,
            fohCapacity: Number(item.fohCapacity),
            title: item.asinMetadata?.title ?? 'Unknown',
            image: item.asinMetadata?.image,
            uphl1: item.asinMetadata?.uph?.uphl1 ?? 'Unknown',
            uphl2: item.asinMetadata?.uph?.uphl2 ?? 'Unknown',
            uphl3: item.asinMetadata?.uph?.uphl3 ?? 'Unknown',
            remainingFacings: isFacingUnitsZero ? 0 : Number(item.facingUnits),
            remainingCapacity: isFacingUnitsZero ? 0 : Number(item.fohCapacity),
            difference: isFacingUnitsZero ? Number(item.facingUnits) * -1 : 0,
            shrinkCost: item.asinMetadata?.performanceData?.shrinkCost ?? 'N/A',
            shrinkPerOps: item.asinMetadata?.performanceData?.shrinkPerOps ?? 'N/A',
            shrinkPerOps13week: item.asinMetadata?.performanceData?.shrinkPerOps13week ?? 'N/A',
            shrinkPerOps26week: item.asinMetadata?.performanceData?.shrinkPerOps26week ?? 'N/A',
            shrinkPerOps52week: item.asinMetadata?.performanceData?.shrinkPerOps52week ?? 'N/A',
            movingAvgSales13week: item.asinMetadata?.performanceData?.movingAvgSales13week ?? 'N/A',
            movingAvgSales26week: item.asinMetadata?.performanceData?.movingAvgSales26week ?? 'N/A',
            movingAvgSales52week: item.asinMetadata?.performanceData?.movingAvgSales52week ?? 'N/A',
          };
        });
        return {
          results,
          unitsToRemove,
          maxFacingUnits,
          occupiedFacingUnits,
          macroplanogramId,
        } as AgileRecommendationResult;
      }),
    [input.batchAssortmentSwap, data?.getBatchAssortmentSwapRecommendations.swapRecommendations],
  );
  useEffect(() => {
    if (!isSuccess) {
      void logger.error(`Error occurred while retrieving GetSwapRecommendation for ${selectedProgramName}`);
    }
  }, [isSuccess, selectedProgramName]);

  return { isLoading, recommendations };
};

export default useGetBatchAssortmentSwapRecommendations;
